import {FC, SyntheticEvent, useEffect, useMemo, useState} from "react";
import classNames from "classnames/dedupe";
import {cn} from "src/helpers/bem";
import "./styles.scss";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormHelperText,
  TextField,
} from "@mui/material";
import {BannerProject, useBannerProjectsSearch} from "src/hooks/banners";
import {useDebouncedValue} from "src/hooks/useDebouncedValue";

const b = cn("banner-projects-field");


const ALL_PROJECTS_ID = "__all_projects__";

const SEARCH_DEBOUNCE_MS = 400;

const SCROLL_THRESHOLD_PX = 200;

interface ProjectOption {
  id: string;
  name: string;
  missing?: boolean;
}

const ALL_PROJECTS_OPTION: ProjectOption = {
  id: ALL_PROJECTS_ID,
  name: "Все проекты",
};


const ALL_PROJECTS_VALUE: ProjectOption[] = [ALL_PROJECTS_OPTION];

export interface BannerProjectsValue {
  allProjects: boolean;
  projectIds: string[];
}

interface Props {
  className?: string;
  allProjects: boolean;
  projectIds: string[];
  initialProjects?: BannerProject[];
  onChange: (value: BannerProjectsValue) => void;
  disabled?: boolean;
  error?: string | null;
}

export const BannerProjectsField: FC<Props> = (props) => {
  const {
    className,
    allProjects,
    projectIds,
    initialProjects,
    onChange,
    disabled = false,
    error,
  } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useBannerProjectsSearch(debouncedSearch);

  // Накопительный словарь id → название. Пополняется из banner.projects и из
  // каждой загруженной страницы выдачи и никогда не чистится: иначе чипы уже
  // выбранных проектов теряли бы названия при смене поиска.
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const incoming = [...(initialProjects ?? []), ...(data?.items ?? [])];
    if (!incoming.length) {
      return;
    }

    setNames((prev) => {
      let changed = false;
      const next = {...prev};

      incoming.forEach((it) => {
        if (it.id && it.name && next[it.id] !== it.name) {
          next[it.id] = it.name;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [initialProjects, data]);


  const selectedNamesKey = projectIds.map((id) => names[id] ?? "").join("\u0000");

  const selected = useMemo<ProjectOption[]>(() => {
    if (allProjects) {
      return ALL_PROJECTS_VALUE;
    }
    return projectIds.map((id) => {
      const name = names[id];
      return name ? {id, name} : {id, name: id, missing: true};
    });

  }, [allProjects, projectIds, selectedNamesKey]);

  const options = useMemo<ProjectOption[]>(() => {
    const loaded = data?.items ?? [];
    const merged: ProjectOption[] = [ALL_PROJECTS_OPTION];
    const seen = new Set<string>([ALL_PROJECTS_ID]);

    // Выбранные обязаны присутствовать в options, иначе MUI не сматчит их с
    // value и отвалится предупреждением о невалидном значении
    selected.forEach((it) => {
      if (!seen.has(it.id)) {
        seen.add(it.id);
        merged.push(it);
      }
    });

    loaded.forEach((it) => {
      if (it.id && !seen.has(it.id)) {
        seen.add(it.id);
        merged.push({id: it.id, name: it.name ?? it.id});
      }
    });

    return merged;
  }, [data, selected]);

  const handleChange = (_: unknown, next: ProjectOption[]) => {
    // Выбор «Все проекты» очищает конкретные проекты
    if (next.some((it) => it.id === ALL_PROJECTS_ID) && !allProjects) {
      onChange({allProjects: true, projectIds: []});
      return;
    }

    // Выбор конкретного проекта выключает «Все проекты», дубли отбрасываем
    const ids = Array.from(
      new Set(next.filter((it) => it.id !== ALL_PROJECTS_ID).map((it) => it.id)),
    );

   
    onChange({allProjects: ids.length === 0, projectIds: ids});
  };

 
  const handleListboxScroll = (event: SyntheticEvent) => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    const node = event.currentTarget as HTMLElement;
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;

    if (remaining < SCROLL_THRESHOLD_PX) {
      fetchNextPage();
    }
  };

  const loadedCount = data?.items.length ?? 0;
  const total = data?.total ?? 0;

  // Счётчик показываем только при раскрытом списке, чтобы не шуметь в форме
  const countText = useMemo(() => {
    if (!total) {
      return null;
    }
    if (loadedCount >= total) {
      return `Найдено проектов: ${total}`;
    }
    return `Показано ${loadedCount} из ${total} — прокрутите список или уточните поиск`;
  }, [loadedCount, total]);

  const loadError = isError ? "Не удалось загрузить список проектов" : null;
  const hint = allProjects ? "Баннер увидят пользователи всех проектов" : null;
  const helperText = error ?? loadError ?? (open ? countText : null) ?? hint;

  return (
    <div className={classNames(b(), className)}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearch("");
        }}
        options={options}
        value={selected}
        onChange={handleChange}
        loading={isFetching}
        disabled={disabled}
        // поиск и пагинация серверные — локальную фильтрацию отключаем
        filterOptions={(all) => all}
        inputValue={search}

        onInputChange={(_, next, reason) => {
          if (reason === "input") {
            setSearch(next);
          } else if (reason === "clear") {
            setSearch("");
          }
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={
          search ? "Проекты не найдены" : "Введите название проекта"
        }
        loadingText="Загрузка проектов…"
        ListboxProps={{onScroll: handleListboxScroll}}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Проекты"
            error={Boolean(error) || isError}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const {key, ...tagProps} = getTagProps({index});
            return (
              <Chip
                key={key}
                size="small"
                label={
                  option.missing ? `${option.name} (недоступен)` : option.name
                }
                color={option.missing ? "warning" : undefined}
                {...tagProps}
              />
            );
          })
        }
      />

      {helperText && (
        <FormHelperText error={Boolean(error) || isError}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};
